import {
  SitecoreImageField,
  SitecoreField,
  SitecoreLookupField,
  SitecoreItem,
  SitecoreMultilistField,
  SitecoreDateField,
  SitecoreNumberField,
  SitecoreCheckboxField,
} from './Common';
import { FormJsonData } from './Form';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

export type Author = {
  name: SitecoreField<string>;
  jobTitle?: SitecoreField<string>;
  jobImage?: SitecoreImageField;
};

export type ArticleTemplate = {
  id: string;
  name: string;
  baseTemplates: { id: string; name: string }[];
};

export type LimitedDisplay = {
  limitedDisplay: {
    targetId: string | null;
  };
  limitedDisplayExceptions: {
    targetIds: string[];
  };
};

export type ArticleCapability = LimitedDisplay & {
  id: string;
  name: SitecoreField<string>;
};

export type ArticleData = LimitedDisplay & {
  id: string;
  name: string;
  articleImage?: SitecoreImageField;
  articleTitle: SitecoreField<string>;
  articleStrapline: SitecoreField<string>;
  contentGroup: SitecoreLookupField<SitecoreItem | null>;
  articleCategory: SitecoreLookupField<SitecoreItem | null>;
  articleAuthor: SitecoreMultilistField<Author>;
  articleContributor: SitecoreMultilistField<SitecoreItem>;
  articleTags?: SitecoreMultilistField<SitecoreItem>;
  articlePublicationDate: SitecoreDateField;
  articleSeriesPart?: SitecoreNumberField;
  articleSeries: SitecoreLookupField<SitecoreItem | null>;
  articleCapabilities?: SitecoreMultilistField<ArticleCapability>;
  duration?: SitecoreNumberField;
  premiumContent?: SitecoreCheckboxField;
  articleRegion: SitecoreLookupField<SitecoreItem | null>;
  articleThemes?: SitecoreMultilistField<SitecoreItem>;
  template: ArticleTemplate;
};

export type ArticleType = 'article' | 'video' | 'podcast';
export type EntityType = 'article' | 'pressRelease' | 'helpArticle';

export type ArticleCardData = {
  id: string;
  name: string;
  template: ArticleTemplate;
  articleCategory: SitecoreLookupField<{
    name: SitecoreField<string>;
  }>;
  articleTitle: SitecoreField<string>;
  articleStrapline: SitecoreField<string>;
  articlePublicationDate: SitecoreDateField;
  articleImage?: SitecoreImageField;
  duration?: SitecoreNumberField;
  articleTags?: SitecoreMultilistField<{
    name: SitecoreField<string>;
  }>;
  articleUrl?: {
    url: string;
  };
};

export type RelatedArticle = {
  id: string;
  name: string;
  articleTitle: SitecoreField<string>;
};

export type ThemeData = {
  name: SitecoreField<string>;
  title: SitecoreField<string>;
  id: string;
};

export const PAGE_TEMPLATE_IDS = {
  ExternalArticle: '9BF4E2D8EFB1413A957A2E427D9A5F81',
  Article: '5AA69853066D416A9FFFE7B0A0B1E245',
  PressRelease: '6B6346A578D14EBE828C85ACBA073AC2',
  Video: '16E7A72E9B6749A1BF78731012D45AC6',
  VideoPressRelease: '331FC25BBB3242798E47F8333441866D',
  Podcast: '02312FC9EDE440FF86065DA068784F2C',
  PodcastPressRelease: 'B0566D63A87B4E899155AA1BAB91FFA8',
  BaseArticle: '70945527D09E4CC5A30A364324CBFCCF',
  BasePressRelease: '94E1D50186424DCF9CEAA0EE86451733',
  BaseHelpArticle: 'D3BECD5C437B44C8BF7FFB15BB5CA09B',
} as const;

export const ID_TO_ARTICLE_TYPE: Record<string, ArticleType> = {
  [PAGE_TEMPLATE_IDS.ExternalArticle]: 'article', // External Article
  [PAGE_TEMPLATE_IDS.Article]: 'article', // Article
  [PAGE_TEMPLATE_IDS.PressRelease]: 'article', // Press Release
  [PAGE_TEMPLATE_IDS.Video]: 'video', // Video
  [PAGE_TEMPLATE_IDS.VideoPressRelease]: 'video', //Video Press Release
  [PAGE_TEMPLATE_IDS.Podcast]: 'podcast', // Press Release
  [PAGE_TEMPLATE_IDS.PodcastPressRelease]: 'podcast', // Podcast Press Release
} as const;

export const ID_TO_ENTITY_TYPE: Record<string, EntityType> = {
  [PAGE_TEMPLATE_IDS.BaseArticle]: 'article', //  BaseArticle
  [PAGE_TEMPLATE_IDS.BasePressRelease]: 'pressRelease', // BasePressRelease
  [PAGE_TEMPLATE_IDS.BaseHelpArticle]: 'helpArticle', // BaseHelpArticle
} as const;

export type CapabilityCard = {
  id: string;
  image: { url: string; alt: string };
  link: string;
  name: string;
  title: string;
};

export type ArticleWildcardConfiguration = {
  capabilityCards?: CapabilityCard[];
  showNextSteps: boolean;
  showRelatedArticles: boolean;
  showRelatedCapabilities: boolean;
  showRelatedStories: boolean;
  premiumFormDataSource: Field<FormJsonData>;
  baseUrl: string;
  pageEdition?: boolean;
};

export type ArticleSeriesType = {
  description: string;
  id: string;
  name: string;
  url: string;
  availableArticlesCount?: number;
  image: {
    alt: string;
    url: string;
  };
};

export function slugToName(articleSlug: string): string;
export function slugToName(articleSlug: undefined): undefined;
export function slugToName(articleSlug?: string): string | undefined;
export function slugToName(articleSlug?: string) {
  return articleSlug?.replaceAll('-', ' ');
}

export function nameToSlug(name?: string) {
  return name?.toLowerCase().replaceAll(' ', '-');
}

export type ArticleSearchResponse = {
  article_authors: Array<string>;
  article_capabilities: Array<string>;
  article_categories: Array<string>;
  article_contributors: Array<string>;
  article_limited_capabilities: Array<string>;
  article_tags: Array<string>;
  article_themes: Array<string>;
  article_content_type: string;
  article_entity_type: string;
  article_title: string;
  article_type: string;
  article_url: string;
  id: string;
  article_duration: number;
  article_publication_date: string;
  article_series_part: number;
  article_is_premium: boolean;
  article_strapline?: string;
};

export type SitecoreSearchResponse<T> = {
  content: Array<T>;
  entity: string;
  offset: number;
  total_item: number;
};

export type LazyQuery<T> = {
  data: SitecoreSearchResponse<T>;
  isFetching: boolean;
  error: any;
};

export type FilterOptionType = {
  aggregateCount: number;
  applied: boolean;
  count: number;
  disabled: boolean;
  selected: boolean;
  showAggregateCount: boolean;
  text: string;
};

export type UpdatedFiltersType = {
  displayName: string;
  displayNameKey: string;
  label: string;
  name: string;
  options: Array<FilterOptionType>;
};
